import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { ContentSpacer } from '../../../../TCSMApp/global/components/layout/generic-content-spacer';
import { GetDetail, Search } from '../../ducks/clergy-api';
import ClergyDetail from './clergy-detail';
import { Clergy, ClergyComplete, ClergyFilters, ClergyQueryStringParameters, ClergySearchSort, IsLocalClergy } from './clergy-model';
import ClergyResults from './clergy-results';
import ClergySearch from './clergy-search';
import ClergySearchAdvanced from './clergy-search-advanced';
import { apiErrorClear } from '../../../../TCSMApp/global/ducks/base-api';
import { MailDestination } from '../mail/mail-model';
import AppMail from '../mail/app-mail';
import { EcdRoutes } from '../../constants/routes';
import { CleanURLSearchParams, getBasePath } from '../../helpers/routing';
import { EcdStates } from '../../../root/ecd-model';
import { useQuery } from '../../../../TCSMApp/global/hooks/useQuery';
import { enumFromValue } from '../../../../TCSMApp/global/helpers/format-helpers';

function AppClergy(){    
    const currentlanguage = useSelector<EcdStates, string>(state => state?.globalization?.currentlanguage);    
    const location = useLocation()?.pathname;
    const basePath = getBasePath(currentlanguage);
    const dispatch = useDispatch();
    const history = useHistory();
    const routeMatch = useRouteMatch();

    const matchResults = matchPath(location, {
        path: `${routeMatch.path}/${EcdRoutes.Results}`,
        exact: true,
        strict: false,
      });

    const searchParams = new URLSearchParams(useQuery());    
    
    const initialResults: Clergy[] = [];
    const initialFilters: ClergyFilters = {};
    
    const queryParamsFilters: ClergyFilters = {
        name: searchParams?.get(ClergyQueryStringParameters.Name),
        first_name: searchParams?.get(ClergyQueryStringParameters.FirstName),
        last_name: searchParams?.get(ClergyQueryStringParameters.LastName),	
        zip_code: searchParams?.get(ClergyQueryStringParameters.ZipCode),
        location: searchParams?.get(ClergyQueryStringParameters.Location),
        c_res: searchParams?.get(ClergyQueryStringParameters.CanonicalResidence),
        is_bishop: searchParams?.get(ClergyQueryStringParameters.IsBishop),        
        sort: enumFromValue(searchParams?.get(ClergyQueryStringParameters.Sort), ClergySearchSort),
        page: searchParams?.get(ClergyQueryStringParameters.Page),        
    };

    const initialClergyDetail: ClergyComplete = {
        id: '',
        full_name: '',
        published_address: undefined,
        canonical_residence: '',
        principal_positions: [],
        ordination_information: undefined,
        received_data: undefined,
        transfer_data: undefined,
    };    

    const initialMailDestination: MailDestination = {
        name: '',
        email: '',
        itemGuid: '',
    };    
    
    const [contextClergyDetail, setContextClergyDetail] = useState(initialClergyDetail);
    const [searchResults, setSearchResults] = useState(initialResults);
    const [contextFilters, setContextFilters] = useState(initialFilters);    
    const [isLastSearchAdvanced, setIsLastSearchAdvanced] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>(null);    
    const [contextMailDestination, setContextMailDestination] = useState(initialMailDestination);
    const [searchInProgress, setSearchInProgress] = useState(false);    

    // Detail Search ------------------------
    function OnDetailSuccess(clergyId: string | number, serverResult, isReplace: boolean = false){
        if (serverResult?.status?.code === 'SUCCESS') {
            setContextClergyDetail({
                ...serverResult?.data,
                id: clergyId,
            });            

            if (isReplace){
                history.replace(`${routeMatch.path}/${clergyId}/${EcdRoutes.Detail}`);    
                return;
            }

            history.push(`${routeMatch.path}/${clergyId}/${EcdRoutes.Detail}`);
        }
        
        if (serverResult?.status?.code === 'ERROR') {            
            setErrorMessage(serverResult?.status?.message);
        }
    }   

    function OnDetailMailSuccess(clergyId: string | number, serverResult){
        if (serverResult?.status?.code === 'SUCCESS') {
            setContextClergyDetail({
                ...serverResult?.data,
                id: clergyId,
            });            
            
            setContextMailDestination(
                {
                    email: serverResult?.data?.email_address,
                    name: serverResult?.data?.full_name,
                    itemGuid: clergyId?.toString(),
                });        
            history.push(`${routeMatch.path}/${clergyId}/${EcdRoutes.Detail}/${EcdRoutes.Mail}`);        
        }
        
        if (serverResult?.status?.code === 'ERROR') {            
            setErrorMessage(serverResult?.status?.message);
        }
    }   
    
    function SelectClergyById(clergyId: string | number){
        if (!clergyId) return;
        
        dispatch(GetDetail(clergyId, (serverResult)=> OnDetailSuccess(clergyId, serverResult)));
    }     
    // -------------------------------------

    // Search ------------------------------
    function ReorderInternationalResults(serverResults: Clergy[]): Clergy[]{
        const localResults = [];
        const internationalResults = [];

        if (serverResults?.length > 0){
            serverResults.forEach(clergy => {
                    if (IsLocalClergy(clergy)){
                        localResults.push(clergy);
                    } else {
                        internationalResults.push(clergy);
                    }
                });
        }
        
        return localResults.concat(internationalResults);
    }

    function OnSearchSucess(filters: ClergyFilters, serverResult, isReplace: boolean = false){
        if (serverResult?.status?.code === 'SUCCESS') {
            setSearchResults(ReorderInternationalResults(serverResult?.data));                        

            const params = new URLSearchParams(
                {
                    ...filters,
                });

            CleanURLSearchParams(params);

            const contextCompare = new URLSearchParams(
                {
                    ...contextFilters,                    
                });
    
            CleanURLSearchParams(contextCompare);                
            
            const contextCompareString = contextCompare?.toString();
            const paramsString = params?.toString();

            // The url is the same, no need to update.
            if (contextCompareString === paramsString) return null;

            if (isReplace){
                history.replace({
                    pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
                    search: params.toString(),
                });    
            } else {
                history.push({
                    pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
                    search: params.toString(),
                });
            }
        }
        
        if (serverResult?.status?.code === 'ERROR') {            
            setErrorMessage(serverResult?.status?.message);
        }      
        
        setSearchInProgress(false);
    }    
    
    function PerformSearch(filters: ClergyFilters, isReplace: boolean = false){
        if (!filters) return;

        setSearchInProgress(true);
        setSearchResults(initialResults);
        setContextFilters(filters);
        dispatch(apiErrorClear());
        dispatch(Search(filters, (serverResult)=> OnSearchSucess(filters, serverResult, isReplace)));
    }

    function SetSort(searchFilters: ClergyFilters, selectedSort : ClergySearchSort){

        const newSort: ClergySearchSort = selectedSort || ClergySearchSort.FirstNameAsc;
        
        const params = new URLSearchParams(
            {
                ...searchFilters,
                sort: newSort,
            });

        CleanURLSearchParams(params);

        history.push({
            pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
            search: params.toString(),
        });
    }

    function ToggleSort(searchFilters: ClergyFilters, currentSort : ClergySearchSort){

        let newSort: ClergySearchSort = ClergySearchSort.FirstNameAsc;

        switch (currentSort) {
            case ClergySearchSort.FirstNameDesc:
            case ClergySearchSort.FirstNameAsc:
                newSort = searchFilters.sort === ClergySearchSort.FirstNameDesc ? ClergySearchSort.FirstNameAsc : ClergySearchSort.FirstNameDesc;                
                break;

            case ClergySearchSort.LastNameAsc:
            case ClergySearchSort.LastNameDesc:
                newSort = searchFilters.sort === ClergySearchSort.LastNameDesc ? ClergySearchSort.LastNameAsc : ClergySearchSort.LastNameDesc;                
                break;

            case ClergySearchSort.StateAsc:
            case ClergySearchSort.StateDesc:
                newSort = searchFilters.sort === ClergySearchSort.StateDesc ? ClergySearchSort.StateAsc : ClergySearchSort.StateDesc;                
                break;

            case ClergySearchSort.CityAsc:
            case ClergySearchSort.CityDesc:
                newSort = searchFilters.sort === ClergySearchSort.CityDesc ? ClergySearchSort.CityAsc : ClergySearchSort.CityDesc;                
                break;
        
            default:
                break;
        }        
        
        SetSort(searchFilters, newSort);
    }

    function OnClearForm(){
        setContextFilters(initialFilters);
    }
    // -------------------------------------

    // Navigate ----------------------------
    function NavigateSearch(){                
        history.push(`${routeMatch.path}/${EcdRoutes.Search}`);
    }

    function NavigateAdvancedSearch(){
        setContextFilters(initialFilters);        
        setIsLastSearchAdvanced(true);        
        history.push(`${routeMatch.path}/${EcdRoutes.AdvancedSearch}`);
    }

    function NavigateBack(){                
        history.goBack();
    }

    function NavigateNewSearch(){
        setContextFilters(initialFilters);        
        setContextClergyDetail(initialClergyDetail);
        setSearchResults(initialResults);

        if (isLastSearchAdvanced){            
            NavigateAdvancedSearch();
        } else {
            NavigateSearch();            
        }
    }

    function NavigateToPageIndex(newPageIndex: number){
        // Created this if in the future the pagination 
        // is going to be done on the server.        
        
        const params = new URLSearchParams(
            {
                ...contextFilters,
                page: newPageIndex?.toString(),
            });

        CleanURLSearchParams(params);

        history.push({
            pathname: `${routeMatch.path}/${EcdRoutes.Results}`,
            search: params.toString(),
        });         
    }

    function NavigateToSendEmail(clergyId: string | number, destination: MailDestination){
        if (!clergyId && !destination?.email) return;

        if (clergyId && !destination?.email){
            dispatch(GetDetail(clergyId, (serverResult)=> { OnDetailMailSuccess(clergyId, serverResult); }));
            return;
        }

        setContextMailDestination(destination);        
        history.push(`${routeMatch.path}/${clergyId}/${EcdRoutes.Detail}/${EcdRoutes.Mail}`);        
    }
    // -------------------------------------    

    // Detail Search ------------------------
    function SelectParishById(parishId: string | number){
        if (!parishId) return;
        
        history.push(`${basePath}/${EcdRoutes.Parish}/${parishId}/${EcdRoutes.Detail}`);        
    }    
    // -------------------------------------

    function SearchError(){
        if (!errorMessage) return null;

        return (<>
            <ContentSpacer>
                <Banner
                    banner={{
                        type: 'error',
                        label: <p><strong>{errorMessage}</strong></p>,
                    }}
                />
            </ContentSpacer>
        </>);
    }    

    useEffect(() => {
        if (!matchResults) return;

        const contextCompare = new URLSearchParams(
            {
                ...contextFilters,
                page: '1',
            });

        const queryCompare = new URLSearchParams(
            {
                ...queryParamsFilters,
                page: '1',
            });

        CleanURLSearchParams(contextCompare);
        CleanURLSearchParams(queryCompare);

        // Only perform search when the paramters change.
        const contextCompareString = contextCompare?.toString();
        const queryCompareString = queryCompare?.toString();

        if (contextCompareString !== queryCompareString && 
            (queryParamsFilters.name ||
                queryParamsFilters.first_name ||
                queryParamsFilters.last_name ||
                queryParamsFilters.location ||
                queryParamsFilters.zip_code ||
                queryParamsFilters.is_bishop ||
                queryParamsFilters.sort ||
                queryParamsFilters.c_res)){
            PerformSearch(queryParamsFilters, true);
        }

        if (contextCompareString === queryCompareString && contextFilters?.page !== queryParamsFilters?.page){
            setContextFilters({
                ...contextFilters,
                page: queryParamsFilters?.page,
            });
        }
	}, [location, queryParamsFilters]);

    return (        
        <>            
            <SearchError />            
            <Suspense fallback={<LoadingCircle />}>
                <Switch>                
                    <Route exact path={`${routeMatch.path}`}>
                        <Redirect to={{ pathname: `${routeMatch.path}/${EcdRoutes.Search}/` }}/>
                    </Route>
                    <Route exact path={`${routeMatch.path}/${EcdRoutes.Search}`}>
                        <ClergySearch performSearch={PerformSearch} selectClergyById={SelectClergyById} 
                            navigateAdvancedSearch={NavigateAdvancedSearch} searchFilters={contextFilters}
                            clearForm={OnClearForm} />
                    </Route>                    
                    <Route exact path={`${routeMatch.path}/${EcdRoutes.AdvancedSearch}`}>
                        <ClergySearchAdvanced performSearch={PerformSearch}
                            searchFilters={contextFilters}
                            clearForm={OnClearForm} />
                    </Route>
                    <Route exact path={`${routeMatch.path}/${EcdRoutes.Results}`}>
                        { contextFilters ? 
                            <ClergyResults searchResults={searchResults} searchFilters={contextFilters} 
                            onNavigateBack={NavigateBack} onNewSearch={NavigateNewSearch} toggleSort={ToggleSort} 
                            currentPageIndex={contextFilters?.page ? Number(contextFilters?.page) : 1} navigateToPageIndex={NavigateToPageIndex}
                            selectClergyById={SelectClergyById} searchInProgress={searchInProgress}
                            setSort={SetSort} />
                            : <LoadingCircle />}                        
                    </Route>                       
                    <Route exact path={`${routeMatch.path}/:clergyid/${EcdRoutes.Detail}`}>                        
                            <ClergyDetail selectedClergy={contextClergyDetail} onNavigateBack={NavigateBack} 
                            onNewSearch={NavigateNewSearch} onSelectParishById={SelectParishById} onSelectClergyById={SelectClergyById}
                            onNavigateToSendEmail={NavigateToSendEmail} />                                                 
                    </Route> 
                    <Route exact path={`${routeMatch.path}/:itemId/${EcdRoutes.Detail}/${EcdRoutes.Mail}`}>
                        <AppMail onNavigateBack={NavigateBack} destination={contextMailDestination} onNavigateToSendEmail={NavigateToSendEmail} isClergy />
                    </Route>                  
                    <Route>                                                
                        <Redirect to={{ pathname: `${routeMatch.path}/${EcdRoutes.Search}/` }}/>
                    </Route>
                </Switch>
            </Suspense>
        </>        
    );
}

export default AppClergy;
